<script setup>
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import { storeToRefs } from 'pinia'
import { useMessageStore } from '~/stores/messages'
import { useParkingStore } from '~/stores/parking'
import useFetchTicketInfo from '~/composables/useFetchTicketInfo'

// Get Auth states
const bearerToken = useState('state_bearerToken')

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get center route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Define ref
const visualEffect = ref(false)

// Fetch parking store
const parkingStore = useParkingStore()

const {
    ticketFormValues,
    isForeignLicensePlateVisible,
    ticketFormValidationStatus,
    savedTicketVoucher
} = storeToRefs(parkingStore)

const {
    updateTicketFormValues,
    toggleLicensePlateVisibility,
    updateTicketFormValidationStatus,
    updateTicketInfos,
    handleGetTicketInfoSuccess,
    handleGetTicketInfoError
} = parkingStore

// Define some refs
const showBarCodeScanner = ref(false)
const dataScrollTrigger = ref(null)

// Check if german license plate is visible
const germanPlateIsVisible = computed(() => !isForeignLicensePlateVisible.value)

// Check if ticket number field is empty
const emptyTicketNumber = computed(() => !ticketFormValues.value?.parkingTicketNumber)

// Add dynamic values (use empty strings, because without this the form is not submitted)
const getLicensePlateCity = computed(() => {
    return (
        germanPlateIsVisible.value &&
        emptyTicketNumber.value &&
        ticketFormValues.value?.licensePlateCity
    )
        ? ticketFormValues.value?.licensePlateCity
        : ''
})

const getLicensePlateChars = computed(() => {
    return (
        germanPlateIsVisible.value &&
        emptyTicketNumber.value &&
        ticketFormValues.value?.licensePlateChars
    )
        ? ticketFormValues.value?.licensePlateChars
        : ''
})

const getLicensePlateNumber = computed(() => {
    return (
        germanPlateIsVisible.value &&
        emptyTicketNumber.value &&
        ticketFormValues.value?.licensePlateNumber
    )
        ? ticketFormValues.value?.licensePlateNumber
        : ''
})

const getLicensePlateForeign = computed(() => {
    return (
        isForeignLicensePlateVisible.value &&
        emptyTicketNumber.value &&
        ticketFormValues.value?.licensePlateForeign
    )
        ? ticketFormValues.value?.licensePlateForeign
        : ''
})

const getParkingTicketNumber = computed(() => ticketFormValues.value?.parkingTicketNumber || '')

// Prepare form (prefill this values with stored data from parking store)
const { values, meta, handleSubmit, isSubmitting } = useForm({
    licensePlateCity: getLicensePlateCity.value,
    licensePlateChars: getLicensePlateChars.value,
    licensePlateNumber: getLicensePlateNumber.value,
    licensePlateForeign: getLicensePlateForeign.value,
    parkingTicketNumber: getParkingTicketNumber.value
})

// Array mit valid user data
const validUserDataArray = computed(() => {
    return Object.values(values).filter(item => !!item)
})

// Show submit button depending on valid user data
const validUserData = computed(() => {
    const validTicketNumber = validUserDataArray.value.length === 1 && (!!values.parkingTicketNumber) && meta.value.valid
    const validGermanLicensePlate = validUserDataArray.value.length === 3 && (!!values.licensePlateCity) && (!!values.licensePlateChars) && (!!values.licensePlateNumber) && meta.value.valid
    const validForeignLicensePlate = validUserDataArray.value.length === 1 && (!!values.licensePlateForeign)

    return validTicketNumber || validGermanLicensePlate || validForeignLicensePlate
})

// Config for license plate city input
const licensePlateCityConfig = computed(() => {
    return {
        fieldType: 'text',
        id: 'parking-license-plate-city',
        name: 'licensePlateCity',
        pending: isSubmitting.value,
        placeholder: staticKeys.value?.plate_example_german_1,
        lightMode: true,
        large: true,
        center: true,
        upperCase: true,
        maxLength: 3,
        emitTarget: true,
        readonly: values.parkingTicketNumber ? 'readonly' : undefined,
        serverError: {
            error: ticketFormValidationStatus.value?.licensePlateGerman === 'error'
        },
        val: getLicensePlateCity.value,
        validationRules: yup.string()
            .matches(/^[a-zA-Z]*$/, staticKeys.value?.parking_ticketpayment_step1_plate_validationerror)
            .max(3, staticKeys.value?.general_error_maxlength.replace('%s', '3'))
    }
})

// Config for license plate chars input
const licensePlateCharsConfig = computed(() => {
    return {
        fieldType: 'text',
        id: 'parking-license-plate-chars',
        name: 'licensePlateChars',
        pending: isSubmitting.value,
        placeholder: staticKeys.value?.plate_example_german_2,
        lightMode: true,
        upperCase: true,
        center: true,
        large: true,
        maxLength: 2,
        emitTarget: true,
        readonly: values.parkingTicketNumber ? 'readonly' : undefined,
        val: getLicensePlateChars.value,
        validationRules: yup.string()
            .matches(/^[a-zA-Z]*$/, staticKeys.value?.parking_ticketpayment_step1_plate_validationerror)
            .max(2, staticKeys.value?.general_error_maxlength.replace('%s', '2')),
        serverError: {
            error: ticketFormValidationStatus.value?.licensePlateGerman === 'error'
        }
    }
})

// Config for license plate number input
const licensePlateNumberConfig = computed(() => {
    return {
        fieldType: 'text',
        id: 'parking-license-plate-number',
        name: 'licensePlateNumber',
        pending: isSubmitting.value,
        placeholder: staticKeys.value?.plate_example_german_3,
        lightMode: true,
        large: true,
        center: true,
        emitTarget: true,
        upperCase: true,
        maxLength: 5,
        readonly: values.parkingTicketNumber ? 'readonly' : undefined,
        val: getLicensePlateNumber.value,
        validationRules: yup.string()
            .matches(/^([0-9]{1,4}[A-Za-z]?|[A-Za-z][0-9]{1,4})?$/, staticKeys.value?.parking_ticketpayment_step1_plate_validationerror)
            .max(5, staticKeys.value?.general_error_maxlength.replace('%s', '5')),
        serverError: {
            error: ticketFormValidationStatus.value?.licensePlateGerman === 'error'
        }
    }
})

// Config for license plate city input
const foreignLicensePlateConfig = computed(() => {
    return {
        fieldType: 'text',
        id: 'parking-license-plate-foreign',
        name: 'licensePlateForeign',
        pending: isSubmitting.value,
        placeholder: staticKeys.value?.plate_example_international,
        readonly: values.parkingTicketNumber ? 'readonly' : undefined,
        lightMode: true,
        large: true,
        emitTarget: true,
        serverError: {
            error: ticketFormValidationStatus.value?.licensePlateForeign === 'error',
            message: staticKeys.value?.parking_ticketpayment_step1_plate_validationerror
        },
        val: getLicensePlateForeign.value
    }
})

// Config for parking ticket number input
const parkingTicketNumberConfig = computed(() => {
    return {
        label: staticKeys.value?.parking_ticketpayment_step1_ticket_headline,
        fieldType: 'text',
        id: 'parking-ticket-number',
        name: 'parkingTicketNumber',
        pending: isSubmitting.value,
        placeholder: staticKeys.value?.parking_ticketpayment_step1_ticket_label,
        helperText: staticKeys.value?.parking_ticketpayment_step1_ticket_hint,
        lightMode: true,
        readonly: (values.licensePlateCity || values.licensePlateChars || values.licensePlateNumber || values.licensePlateForeign) ? 'readonly' : undefined,
        maxLength: 20,
        large: true,
        scanner: true,
        emitTarget: true,
        visualFeedback: visualEffect.value,
        serverError: {
            error: ticketFormValidationStatus.value?.parkingTicketNumber === 'error',
            message: staticKeys.value?.parking_ticketpayment_step1_ticket_validationerror
        },
        val: getParkingTicketNumber.value,
        validationRules: yup.string()
            .max(20, staticKeys.value?.general_error_maxlength.replace('%s', '20'))
    }
})

// Config for submit button
const submitButtonConfig = computed(() => {
    return {
        visible: true,
        label: staticKeys.value?.parking_ticketpayment_step1_cta,
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            isPending: isSubmitting.value,
            large: true,
            active: validUserData.value
        }
    }
})

// Config for foreign license plate switch
const switchConfig = computed(() => {
    return {
        id: 'parking-license-plate-switch',
        val: isForeignLicensePlateVisible.value ? 'parkingLicensePlateSwitch' : undefined,
        name: 'parkingLicensePlateSwitch'
    }
})

// Get scanner config
const getScannerConfig = computed(() => {
    return {
        isVisible: showBarCodeScanner.value,
        title: staticKeys.value?.parking_ticketpayment_step1_barcode_title
    }
})

// Check if form is submitting
// Get form class
const getFormClass = computed(() => {
    return {
        'is-submitting': isSubmitting.value
    }
})

const getLicensePlateClass = computed(() => {
    return {
        'is-invalid': values.parkingTicketNumber
    }
})

const getTicketNumberClass = computed(() => {
    return {
        'is-invalid': (values.licensePlateCity || values.licensePlateChars || values.licensePlateNumber || values.licensePlateForeign)
    }
})

/**
 * Function to handle toggling the switch.
 *
 * @param {boolean} value - The new value for showing the foreign license plate
 */
const handleToggleSwitch = (value) => {
    toggleLicensePlateVisibility(value)
}

/**
 * Updates the ticket validation form with data emitted to the parking store.
 *
 * @param {Object} target - The target object containing the data to emit.
 */
const emitDataToParkingStore = (target) => {
    updateTicketFormValues({
        [target.name]: target.value
    })
}

/**
 * Handle server error by updating ticket validation status.
 *
 * @param {Object} target - the target object to update
 */
const handleServerError = (target) => {
    const germanPlate = ['licensePlateCity', 'licensePlateChars', 'licensePlateNumber']

    const inputName = germanPlate.includes(target.name) ? 'licensePlateGerman' : target.name

    // Update ticket validation status
    updateTicketFormValidationStatus({
        [inputName]: ''
    })
}

/**
 * Function to handle the barcode scanner.
 *
 * @param {any} value - The value to set for the barcode scanner.
 * @return {void}
 */
const toggleBarCodeScannerVisibility = (value) => {
    showBarCodeScanner.value = value
}

/**
 * Updates the ticket validation status and form based on the barcode scanner result.
 *
 * @param {any} result - The result from the barcode scanner.
 */
const handleBarCodeScannerResult = (result) => {
    updateTicketFormValidationStatus({
        [parkingTicketNumberConfig.value.name]: result?.length > 20 ? 'error' : 'success'
    })

    updateTicketFormValues({
        [parkingTicketNumberConfig.value.name]: result
    })
}

const isSubmitTriggered = ref(false)

// Define german license plate
const germanLicensePlate = ['licensePlateCity', 'licensePlateChars', 'licensePlateNumber']

// Compute payload for fetch ticket infos
const payload = computed(() => {
    const searchTerm = germanLicensePlate.some(key => values[key])
        ? values['licensePlateCity'] + values['licensePlateChars'] + values['licensePlateNumber']
        : Object.entries(values).find(([_, value]) => value)?.[1] || ''

    const searchTermType = values['parkingTicketNumber'] ? 'byTicketNumber' : 'byLicensePlateNumber'

    return {
        isSubmitTriggered: isSubmitTriggered.value,
        bearerToken,
        searchTerm,
        searchTermType,
        centerUid: centerRoute.value,
        voucher: savedTicketVoucher.value
    }
})

// Fetch ticket infos (we have to use reactive payload to trigger refetch)
const { refetch } = useFetchTicketInfo(payload)

/**
 *  Function to handle form submit.
 * @type {(e?: Event) => Promise<Promise<boolean|undefined> | undefined>}
 */
const onSubmit = handleSubmit(async () => {
    // Check if user has entered valid data
    if (!validUserData.value) return

    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.PARKING_TICKET_PAYMENT_STEP1_CLICK,
    })

    // Populate payment infos with searchTerm and searchTermType
    updateTicketInfos({
        searchTermType: payload.value?.searchTermType,
        searchTerm: payload.value?.searchTerm,
    })

    isSubmitTriggered.value = true

    // Trigger refetch to get ticket infos
    const { data, isSuccess, isError } = await refetch()

    // Handle success
    if (isSuccess) {
        handleGetTicketInfoSuccess({ data })
    }

    // Handle (server) errors in a specific way (do not throw common error page)
    if (isError) {
        const context = germanLicensePlate.some(key => values[key]) ? 'licensePlateGerman' : Object.keys(values).find(key => values[key]) || ''

        handleGetTicketInfoError({
            message: staticKeys.value?.parking_step2_pay_error,
            context
        })
    }
})

/**
 * Watch show bar code scanner
 *
 * @param {boolean} value - The new value for showing the barcode scanner
 */
watch(showBarCodeScanner, (value) => {
    if (value) {
        // Reset visual effect
        visualEffect.value = false

        // Remove server validation error while scanning new barcode
        updateTicketFormValidationStatus({
            [parkingTicketNumberConfig.value.name]: ''
        })

        // If error, reset value of form field otherwise keep value
        updateTicketFormValues({
            [parkingTicketNumberConfig.value.name]: parkingTicketNumberConfig.value.val?.length > 20 ? '' : parkingTicketNumberConfig.value.val
        })
    } else {
        setTimeout(() => {
            // Scroll to target
            dataScrollTrigger.value?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            })

            // Set visual effect with timeout
            if (ticketFormValidationStatus.value?.parkingTicketNumber === 'success') {
                visualEffect.value = true
            }
        }, 1000)
    }
}, { deep: true, immediate: false })

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.PARKING_TICKET_PAYMENT_STEP1,
})
</script>

<template>
    <form class="parking-payment-form" :class="getFormClass" autocomplete="off" @submit.prevent="onSubmit">
        <DevOnly>
            <pre>{{ payload }}</pre>
        </DevOnly>

        <DefaultContentBox :content-box-config="{ type: 'small' }" class="parking-payment-form-container">
            <div :class="getLicensePlateClass" class="parking-payment-licence-plate-container">
                <h3>{{ staticKeys.parking_ticketpayment_step1_plate }}</h3>

                <div v-if="isForeignLicensePlateVisible" class="parking-payment-licence-plate-input-container">
                    <FormText
                        :input-config="foreignLicensePlateConfig"
                        @update:update-target="emitDataToParkingStore"
                        @update:update-server-error="handleServerError"
                    />
                </div>

                <template v-else>
                    <div class="parking-payment-licence-plate-input-container">
                        <FormText
                            :input-config="licensePlateCityConfig"
                            @update:update-target="emitDataToParkingStore"
                            @update:update-server-error="handleServerError"
                        />
                        <FormText
                            :input-config="licensePlateCharsConfig"
                            @update:update-target="emitDataToParkingStore"
                            @update:update-server-error="handleServerError"
                        />
                        <FormText
                            :input-config="licensePlateNumberConfig"
                            @update:update-target="emitDataToParkingStore"
                            @update:update-server-error="handleServerError"
                        />
                    </div>

                    <div class="parking-payment-licence-plate-error">
                        <FormFieldMessage
                            :msg-config="{
                                message: staticKeys.parking_ticketpayment_step1_plate_validationerror,
                                error: ticketFormValidationStatus?.licensePlateGerman === 'error'
                            }"
                        />
                    </div>
                </template>

                <div class="parking-payment-foreign-licence-plate-container">
                    <span class="parking-payment-foreign-licence-plate-label">
                        {{ staticKeys.parking_createprepaid_step2_slidertext }}
                    </span>

                    <FormToggleSwitch
                        :form-toggle-switch-config="switchConfig"
                        @update:update-value="handleToggleSwitch"
                    />
                </div>
            </div>

            <span ref="dataScrollTrigger" class="form-divider-label">
                {{ staticKeys.parking_ticketpayment_step1_ticketorplate }}
            </span>

            <div :class="getTicketNumberClass" class="parking-payment-ticket-number-container">
                <FormText
                    :input-config="parkingTicketNumberConfig"
                    @update:update-target="emitDataToParkingStore"
                    @update:update-server-error="handleServerError"
                    @update:toggle-barcode-scanner-visibility="toggleBarCodeScannerVisibility"
                />
            </div>
        </DefaultContentBox>

        <Transition name="fade">
            <BarCodeScanner
                v-if="showBarCodeScanner"
                :scanner-config="getScannerConfig"
                @update:toggle-barcode-scanner-visibility="toggleBarCodeScannerVisibility"
                @update:emit-barcode-scanner-result="handleBarCodeScannerResult"
            />
        </Transition>

        <FormButton :form-button-config="submitButtonConfig" />
    </form>
</template>

<style lang="scss">
.parking-payment-form {
    margin-block: px2rem(24) px2rem(60);

    @include bp(medium1) {
        margin-block: px2rem(40) px2rem(60);
    }

    @include bp(large1) {
        margin-block: px2rem(40) px2rem(100);
    }

    &.is-submitting {
        @include form-is-submitting;
    }
}

.parking-payment-form-container {
    margin-block-end: px2rem(60);
}

.parking-payment-licence-plate-container {
    padding-block-end: px2rem(30);
    border-bottom: 2px solid $parking-payment-header-border-color;
    width: 100%;

    &.is-invalid {
        opacity: 0.5;
        pointer-events: none;
    }

    h3 {
        @include form-label;
    }
}

.parking-payment-foreign-licence-plate-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
}

.parking-payment-foreign-licence-plate-label {
    @include helper-text($margin: 0);
}

.parking-payment-licence-plate-input-container {
    display: flex;
    gap: 10px;

    > div {
        width: 100%;
    }
}

.parking-payment-licence-plate-error {
    width: 100%;
    margin-block: -1rem 12px;
}

.form-divider-label {
    width: 100%;
    text-align: center;

    @include helper-text($margin: 10px 0 20px);
}

.parking-payment-ticket-number-container {
    width: 100%;

    &.is-invalid {
        opacity: 0.5;
        pointer-events: none;
    }

    h3 {
        @include form-label;
    }
}
</style>
